import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import ColorPicker from "../ColorPicker";
import { BRANDING_RESTRICTED_FRONTEND_FIELDS } from "../../utils/constants";
// NOTE: avatar_url is internally used; mapped to bot_avatar_url for API
// (See WIDGET_FIELD_MAPPINGS in utils/constants.js for field name conventions)

/**
 * Filter validation errors for branding fields when they're locked.
 * Returns all errors if branding is enabled, or filters them out if disabled/loading.
 * This prevents confusing error messages on locked inputs.
 */
function filterBrandingErrors(errors, isEnabled) {
  if (isEnabled) return errors;
  const filtered = { ...errors };
  BRANDING_RESTRICTED_FRONTEND_FIELDS.forEach((field) => {
    delete filtered[field];
  });
  return filtered;
}

const BrandingStep = ({
  formData,
  errors,
  onInputChange,
  userPlan,
  planFetchError,
  planLoading = false,
}) => {
  const canCustomizeBranding = userPlan?.allowBrandingCustomization ?? false;
  const filteredErrors = filterBrandingErrors(
    errors,
    canCustomizeBranding && !planLoading,
  );
  const disabledTitle = canCustomizeBranding
    ? undefined
    : "Upgrade your plan to customize branding";

  const renderDisabledNotice = () => {
    // Show loading state while fetching plan
    if (planLoading) {
      return (
        <div className="branding-locked-notice">
          <strong>Loading plan information...</strong>
          <p>Please wait while we check your branding customization options.</p>
        </div>
      );
    }

    // Show API error if plan fetch failed
    if (planFetchError) {
      return (
        <div className="branding-locked-notice branding-error-notice">
          <strong>⚠️ Could not load your plan information.</strong>
          <p>{planFetchError}</p>
          <p style={{ fontSize: "0.8rem", marginTop: "8px" }}>
            Branding features are disabled as a precaution. Please refresh the
            page to try again.
          </p>
        </div>
      );
    }

    // Show plan restriction message if branding is not allowed
    if (!canCustomizeBranding) {
      return (
        <div className="branding-locked-notice">
          <strong>
            Custom branding is not available on your current plan.
          </strong>
          <p>
            Upgrade your subscription to customize colors, avatar, welcome
            message, and other branding options.
          </p>
          <p>
            <Link to="/settings" className="branding-upgrade-link">
              View upgrade options →
            </Link>
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="widget-form-section">
      {renderDisabledNotice()}

      <div className="widget-form-field">
        <ColorPicker
          label="Primary Color"
          value={formData.primary_color}
          onChange={(color) => onInputChange("primary_color", color)}
          showPresets={true}
          disabled={!canCustomizeBranding}
        />
        {filteredErrors.primary_color && (
          <span className="widget-form-error">
            {filteredErrors.primary_color}
          </span>
        )}
        <small>Main theme color for the widget</small>
      </div>

      <div className="widget-form-field">
        <ColorPicker
          label="Secondary Color"
          value={formData.secondary_color}
          onChange={(color) => onInputChange("secondary_color", color)}
          showPresets={true}
          disabled={!canCustomizeBranding}
        />
        {filteredErrors.secondary_color && (
          <span className="widget-form-error">
            {filteredErrors.secondary_color}
          </span>
        )}
        <small>Secondary theme color</small>
      </div>

      <div className="widget-form-field">
        <label htmlFor="avatar_url">Avatar URL</label>
        <input
          id="avatar_url"
          type="url"
          value={formData.avatar_url}
          onChange={(e) => onInputChange("avatar_url", e.target.value)}
          placeholder="https://example.com/avatar.png"
          className={filteredErrors.avatar_url ? "error" : ""}
          disabled={!canCustomizeBranding}
          title={disabledTitle}
        />
        {filteredErrors.avatar_url && (
          <span className="widget-form-error">{filteredErrors.avatar_url}</span>
        )}
        <small>Avatar image for the assistant (optional)</small>
      </div>

      <div className="widget-form-field">
        <label htmlFor="welcome_message">
          Welcome Message <span className="required">*</span>
        </label>
        <textarea
          id="welcome_message"
          value={formData.welcome_message}
          onChange={(e) => onInputChange("welcome_message", e.target.value)}
          placeholder="Hello! How can I help you today?"
          rows={3}
          className={filteredErrors.welcome_message ? "error" : ""}
          disabled={!canCustomizeBranding}
          title={disabledTitle}
        />
        {filteredErrors.welcome_message && (
          <span className="widget-form-error">
            {filteredErrors.welcome_message}
          </span>
        )}
        <small>First message shown to users</small>
      </div>

      <div className="widget-form-field">
        <label htmlFor="placeholder_text">
          Placeholder Text <span className="required">*</span>
        </label>
        <input
          id="placeholder_text"
          type="text"
          value={formData.placeholder_text}
          onChange={(e) => onInputChange("placeholder_text", e.target.value)}
          placeholder="Type your message..."
          className={filteredErrors.placeholder_text ? "error" : ""}
          disabled={!canCustomizeBranding}
          title={disabledTitle}
        />
        {filteredErrors.placeholder_text && (
          <span className="widget-form-error">
            {filteredErrors.placeholder_text}
          </span>
        )}
        <small>Placeholder in the message input field</small>
      </div>

      <div className="widget-form-field">
        <label htmlFor="branding_text">Branding Text</label>
        <input
          id="branding_text"
          type="text"
          value={formData.branding_text}
          onChange={(e) => onInputChange("branding_text", e.target.value)}
          placeholder="Powered by Kwibl"
          disabled={!canCustomizeBranding}
          title={disabledTitle}
        />
        <small>Text shown at the bottom of the widget</small>
      </div>

      <div className="widget-form-field">
        <label htmlFor="branding_url">Branding URL</label>
        <input
          id="branding_url"
          type="url"
          value={formData.branding_url}
          onChange={(e) => onInputChange("branding_url", e.target.value)}
          placeholder="https://example.com"
          className={filteredErrors.branding_url ? "error" : ""}
          disabled={!canCustomizeBranding}
          title={disabledTitle}
        />
        {filteredErrors.branding_url && (
          <span className="widget-form-error">
            {filteredErrors.branding_url}
          </span>
        )}
        <small>Link when branding text is clicked (optional)</small>
      </div>

      <div className="widget-form-field">
        <label htmlFor="show_branding">
          <input
            id="show_branding"
            type="checkbox"
            checked={formData.show_branding !== false}
            onChange={(e) => onInputChange("show_branding", e.target.checked)}
            disabled={!canCustomizeBranding}
            title={disabledTitle}
          />{" "}
          Show branding
        </label>
        <small>Display branding text at the bottom of the widget</small>
      </div>
    </div>
  );
};

BrandingStep.propTypes = {
  formData: PropTypes.shape({
    primary_color: PropTypes.string,
    secondary_color: PropTypes.string,
    avatar_url: PropTypes.string,
    welcome_message: PropTypes.string,
    placeholder_text: PropTypes.string,
    branding_text: PropTypes.string,
    branding_url: PropTypes.string,
    show_branding: PropTypes.bool,
  }).isRequired,
  errors: PropTypes.object.isRequired,
  onInputChange: PropTypes.func.isRequired,
  userPlan: PropTypes.shape({
    allowBrandingCustomization: PropTypes.bool,
  }),
  planFetchError: PropTypes.string,
  planLoading: PropTypes.bool,
};

export default BrandingStep;
